import { create } from 'zustand';
import type { Entitlement, Me, Plan, Wallet } from '../api/types.ts';

export type SessionKind = 'supabase' | 'dev';

export interface Session {
  kind: SessionKind;
  userId: string;
  email: string | null;
}

/**
 * Signed-in state and the cached `/v1/me` (used for offline UI gating until `graceUntil`;
 * the server always has the final say on AI and sync).
 */
export interface AccountState {
  ready: boolean;
  session: Session | null;
  me: Me | null;
  meFetchedAt: number | null;
  /** Last /v1/me attempt failed to reach the server (offline / server down). */
  offline: boolean;
  /** Last /v1/me attempt reached the server but was refused (e.g. 409 dev_identity_conflict). */
  meError: { status: number; code: string; message: string } | null;
  /** Workspace shown in the library and used for new projects: personal or org scope id. */
  activeScope: string | null;
}

export const useAccount = create<AccountState>(() => ({
  ready: false,
  session: null,
  me: null,
  meFetchedAt: null,
  offline: false,
  meError: null,
  activeScope: null,
}));

export const signedIn = (s: AccountState = useAccount.getState()) => s.session !== null;

export function entitlementFor(me: Me | null, scope: string | null): Entitlement | null {
  if (!me || !scope) return null;
  return me.entitlements.find((e) => e.scope === scope) ?? null;
}

export function walletFor(me: Me | null, scope: string | null): Wallet | null {
  if (!me || !scope) return null;
  return me.wallets.find((w) => w.scope === scope) ?? null;
}

/** Credits the signed-in user can spend from a wallet: on Team, their own monthly allowance plus the shared packs. */
export function spendable(w: Wallet): number {
  return w.memberAllowanceLeft == null ? w.balance : w.memberAllowanceLeft + w.purchased + (w.beta ?? 0);
}

export function planFor(me: Me | null, scope: string | null): Plan {
  return entitlementFor(me, scope)?.plan ?? 'free';
}

/** Scope name for UI: "Personal" or the org name. */
export function scopeName(me: Me | null, scope: string | null): string {
  if (!me || !scope || scope === me.personalScope) return 'Personal';
  return me.orgs.find((o) => o.id === scope)?.name ?? 'Workspace';
}

/** True while the cached /v1/me may still be trusted for UI gating. */
export function meUsable(me: Me | null, now = Date.now()): boolean {
  return !!me && Date.parse(me.graceUntil) > now;
}
